import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Howl } from 'howler';
import Lottie from 'react-lottie';
// import * as downloadAnimation from '../../../../../themes/lottieAnimations/downloadCloud.json';

import downloadAnimation from '../../../assets/lottie/DownloadCloud.json';
const AudioPlayer = forwardRef(
  (
    {
      audioAvailable,
      title = '',
      type = 'place',
      description = '',
      thumbnailImage,
      audioUrl,
      subtitleUrl,
      onAudioEnd,
    },
    ref
  ) => {
    const [sound, setSound] = useState(null);
    const [duration, setDuration] = useState(0);
    const [position, setPosition] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subtitles, setSubtitles] = useState([]);
    const [currentSubtitle, setCurrentSubtitle] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const intervalRef = useRef(null);
    const currentCueIndexRef = useRef(-1);

    useImperativeHandle(ref, () => ({
      stopAudioExternally: () => {
        cleanupSound();
      },
    }));

    const cleanupSound = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (sound) {
        sound.stop();
        sound.unload();
        setSound(null);
      }

      setIsPlaying(false);
      setPosition(0);
      setDuration(0);
    };

    useEffect(() => {
      setError(null);
      setCurrentSubtitle('');
      currentCueIndexRef.current = -1;
      cleanupSound();

      if (audioUrl) {
        loadAndPlay();
      }

      return () => {
        cleanupSound();
      };
    }, [audioUrl, subtitleUrl]);

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const parseSRT = (srt) => {
      const cues = [];
      srt
        .trim()
        .split('\n\n')
        .forEach((block) => {
          const [_, times, ...textLines] = block.split('\n');
          if (!times) return;
          const [start, end] = times.split(' --> ').map((t) => {
            const [h, m, sms] = t.replace(',', '.').split(':');
            return +h * 3600 + +m * 60 + parseFloat(sms);
          });
          cues.push({ start, end, text: textLines.join(' ') });
        });
      return cues;
    };

    const updateSubtitle = (time, subs) => {
      const index = subs.findIndex((c) => time >= c.start && time <= c.end);
      if (index !== -1 && index !== currentCueIndexRef.current) {
        currentCueIndexRef.current = index;
        setCurrentSubtitle(subs[index].text);
      } else if (index === -1 && currentCueIndexRef.current !== -1) {
        currentCueIndexRef.current = -1;
        setCurrentSubtitle('');
      }
    };

    const loadAndPlay = async () => {
      setIsLoading(true);
      currentCueIndexRef.current = -1;
      let parsedSubs = [];

      try {
        if (subtitleUrl) {
          const res = await fetch(subtitleUrl);
          const text = await res.text();
          parsedSubs = parseSRT(text);
          setSubtitles(parsedSubs);
        }

        const newSound = new Howl({
          src: [audioUrl],
          html5: true,
          onload: () => {
            setSound(newSound);
            setDuration(newSound.duration());
            setIsLoading(false);
            setIsPlaying(true);
            newSound.play();

            intervalRef.current = setInterval(() => {
              const currentPosition = newSound.seek();
              setPosition(currentPosition);
              if (parsedSubs.length > 0) updateSubtitle(currentPosition, parsedSubs);
            }, 300);
          },
          onend: () => {
            setIsPlaying(false);
            cleanupSound();
            onAudioEnd?.();
          },
          onerror: () => {
            setError('Failed to load the audio!');
            setIsLoading(false);
          }
        });
      } catch (err) {
        console.warn('Load failed:', err.message);
        setError('Failed to load the audio!');
        setIsLoading(false);
      }
    };

    const togglePlay = () => {
      if (!sound) return;

      if (isPlaying) {
        sound.pause();
        setIsPlaying(false);
      } else {
        sound.play();
        setIsPlaying(true);
      }
    };

    const handleSeek = (value) => {
      if (sound) {
        sound.seek(value);
        setPosition(value);
      }
    };

    const handleRewind = () => {
      if (sound) {
        const newPosition = Math.max(0, position - 10);
        sound.seek(newPosition);
        setPosition(newPosition);
      }
    };

    const handleForward = () => {
      if (sound) {
        const newPosition = Math.min(duration, position + 10);
        sound.seek(newPosition);
        setPosition(newPosition);
      }
    };

    const lottieOptions = {
      loop: true,
      autoplay: true,
      animationData: downloadAnimation,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice'
      }
    };

    return (
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 rounded-t-xl shadow-xl z-50 transition-transform duration-300 transform translate-y-0">
        <button 
          className="absolute -top-8 right-4 bg-gray-600 border-2 border-gray-800 rounded-full w-9 h-9 flex items-center justify-center z-50"
          onClick={onAudioEnd}
        >
          <span className="text-white">×</span>
        </button>

        <div className="p-5 w-full">
          <div className="flex items-center mb-4">
            {thumbnailImage && (
              <img 
                src={thumbnailImage} 
                alt="Thumbnail" 
                className="w-16 h-16 rounded-full mr-3 object-cover"
              />
            )}
            <div className="flex-1">
              <p className="text-xs text-gray-300 mb-1">🔊 You're listening to</p>
              <h3 className="text-sm font-semibold text-white line-clamp-2">{title}</h3>
              
              {subtitles.length > 0 ? (
                <p className="text-xs text-gray-300 min-h-6 line-clamp-2">{currentSubtitle}</p>
              ) : (
                description && <p className="text-xs text-gray-300">{description}</p>
              )}
            </div>
          </div>

          {audioAvailable ? (
            <>
              <input
                type="range"
                min="0"
                max={duration || 100}
                value={position}
                onChange={(e) => handleSeek(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer mb-2"
                style={{
                  background: `linear-gradient(to right, #fff 0%, #fff ${(position / (duration || 100)) * 100}%, #666 ${(position / (duration || 100)) * 100}%, #666 100%)`
                }}
              />

              {error ? (
                <div className="text-center py-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              ) : isLoading ? (
                <div className="flex justify-center items-center py-4">
                  <Lottie 
                    options={lottieOptions}
                    height={60}
                    width={60}
                    style={{ transform: 'scale(1.8)' }}
                  />
                </div>
              ) : (
                <div className="bg-black rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center w-1/4">
                      <span className="text-white text-sm">{formatTime(position)}</span>
                      <span className="text-gray-300 text-sm"> / {formatTime(duration)}</span>
                    </div>

                    <div className="flex items-center justify-center w-2/4">
                      <button 
                        onClick={handleRewind}
                        className="text-gray-300 hover:text-white mr-4"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
                        </svg>
                      </button>

                      <button 
                        onClick={togglePlay}
                        className="text-white hover:text-gray-200"
                      >
                        {isPlaying ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </button>

                      <button 
                        onClick={handleForward}
                        className="text-gray-300 hover:text-white ml-4"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
                        </svg>
                      </button>
                    </div>

                    <div className="w-1/4 text-right">
                      <p className="text-gray-300 text-xs truncate">
                        {audioUrl ? audioUrl.split('/').pop() : 'No file selected'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-red-400 text-sm">Audio guide is currently unavailable for selected language.</p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default AudioPlayer;